/*
 * Timeline — Intro behaviour, two independent concerns on one element:
 *
 * 1. Collage parallax (ported from fluevog-our-story.js). Each collage image
 *    declares a data-depth (px); as the band scrolls through the viewport every
 *    image is translated vertically by a fraction of its depth, so images on
 *    different "grounds" drift at different rates. Single rAF loop, runs only
 *    while on screen, respects reduced-motion.
 *
 * 2. Years rail scroll-spy. Each year button may carry a data-target (the id of
 *    the decade section it represents). Whichever target is nearest the top of
 *    the viewport gets its year marked .is-active. Clicking a year smooth-scrolls
 *    to its target. With no targets present yet, the authored default stays lit.
 */
class FluevogTimelineIntro extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.items = Array.from(this.querySelectorAll('[data-depth]'));
    this.years = Array.from(this.querySelectorAll('[data-timeline-year]'));

    this.parallaxEnabled = this.dataset.parallax === 'true' && !this.reducedMotion && this.items.length > 0;

    this.targets = this.years
      .map((year) => {
        const id = year.dataset.target;
        const el = id ? document.getElementById(id) : null;
        return el ? { year, el } : null;
      })
      .filter(Boolean);

    this.visible = false;
    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    if (this.parallaxEnabled) {
      this.items.forEach((item) => {
        const rotate = getComputedStyle(item).getPropertyValue('--fvtl-item-rotate').trim() || '0deg';
        item.dataset.rotate = rotate;
      });
    }

    this.setupYears();

    this.observer = new IntersectionObserver(
      (entries) => {
        this.visible = entries.some((e) => e.isIntersecting);
        if (this.visible) this.onScroll();
      },
      { rootMargin: '15% 0px' }
    );
    this.observer.observe(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.observer?.disconnect();
  }

  setupYears() {
    this.years.forEach((year) => {
      year.addEventListener('click', () => {
        const id = year.dataset.target;
        const el = id ? document.getElementById(id) : null;
        if (el) {
          el.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
        }
      });
    });
  }

  setActiveYear(year) {
    if (year === this.activeYear) return;
    this.activeYear = year;
    this.years.forEach((y) => y.classList.toggle('is-active', y === year));
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(this.update);
  }

  update() {
    this.ticking = false;
    const vh = window.innerHeight || document.documentElement.clientHeight;

    // Scroll-spy: pick the target whose top is closest to (but not far past) the
    // viewport's upper third. Runs regardless of band visibility so the rail keeps
    // up while the reader is deep in the decades below.
    if (this.targets.length > 0) {
      const line = vh * 0.33;
      let best = null;
      let bestDelta = Infinity;
      this.targets.forEach((t) => {
        const top = t.el.getBoundingClientRect().top;
        const delta = Math.abs(top - line);
        if (top - line <= 1 && delta < bestDelta) {
          bestDelta = delta;
          best = t;
        }
      });
      if (!best) best = this.targets[0];
      this.setActiveYear(best.year);
    }

    // Parallax: only while the band is on screen.
    if (this.parallaxEnabled && this.visible) {
      const rect = this.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const progress = (center - vh / 2) / ((vh + rect.height) / 2);
      const clamped = Math.max(-1, Math.min(1, progress));

      this.items.forEach((item) => {
        const depth = parseFloat(item.dataset.depth || '0');
        const shift = (-clamped * depth).toFixed(2);
        item.style.transform = `translate3d(0, ${shift}px, 0) rotate(${item.dataset.rotate})`;
      });
    }
  }
}

if (!customElements.get('fv-timeline-intro')) {
  customElements.define('fv-timeline-intro', FluevogTimelineIntro);
}
